
import BodyScrollLock from './components/BodyScrollLock';
// Dashboard.js - AVS Cloud File Management Dashboard
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ShareLinkModal from './ShareLinkModal';
import ShareLinksManager from './ShareLinksManager';
import AdminPanel from './AdminPanel';
import UserAvatar from './UserAvatar';
import TeamManager from './TeamManager';

function Dashboard({ token, user, onBackToUpload, onLogout, activeTeamContext, onTeamContextChange, bannerStatus, onFolderContextChange }) {
  // State Management
  const [files, setFiles] = useState([]);
  const [toast, setToast] = useState(null);
  const [activeDownloads, setActiveDownloads] = useState({});
  const showToast = (msg, type = 'error') => { setToast({ message: msg, type }); setTimeout(() => setToast(null), 5000); };
  const [renameModal, setRenameModal] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [usage, setUsage] = useState({
    total_files: 0,
    total_storage: 0,
    storage_limit: 10737418240 // 10 GB default
  });
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFiles, setTotalFiles] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const ITEMS_PER_PAGE = 50;
  
  // Admin State
  const [isOwner, setIsOwner] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  
  // UI State
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date', 'name', 'size'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list'
  const [activeFolder, setActiveFolder] = useState('all'); // 'all', 'today', 'week', 'month', 'images', 'videos', 'documents'
  
  // Modal State
  const [shareModalFile, setShareModalFile] = useState(null);
  const [deleteConfirmFile, setDeleteConfirmFile] = useState(null);
  const [showShareLinksManager, setShowShareLinksManager] = useState(false);

  // Bulk Delete State
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);

  // Image Preview State
  const [imagePreviewModal, setImagePreviewModal] = useState(null);
  const [imageCache, setImageCache] = useState({});

  // Video Player State
  const [videoPlayerModal, setVideoPlayerModal] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);

  // Audio Player State
  const [audioPlayerModal, setAudioPlayerModal] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);

  // File Info Modal State
  const [infoModalFile, setInfoModalFile] = useState(null);
  const [fileMetadata, setFileMetadata] = useState(null);
  const [loadingMetadata, setLoadingMetadata] = useState(false);

  // Abuse Report State (für Share-Links die ANDERE Nutzer erstellt haben — hier nicht verwendet,
  // aber State für Admin-Sicht auf eigene Share-Links mit Abuse-Hinweis-Banner)
  const [abuseReportModal, setAbuseReportModal] = useState(null); // { share_token }
  const [abuseType, setAbuseType] = useState('');
  const [abuseDescription, setAbuseDescription] = useState('');
  const [abuseEmail, setAbuseEmail] = useState('');
  const [abuseSubmitting, setAbuseSubmitting] = useState(false);
  const [abuseSuccess, setAbuseSuccess] = useState(false);

  // Team State
  const [showTeamManager, setShowTeamManager] = useState(false);
  // Initialize from App.js state so context survives view switches
  const [teamContext, setTeamContext] = useState(activeTeamContext ?? null);
  const [pendingInvCount, setPendingInvCount] = useState(0);
  const [teamFiles, setTeamFiles] = useState([]);
  const [loadingTeamFiles, setLoadingTeamFiles] = useState(false);

  // Folder State
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [currentFolderName, setCurrentFolderName] = useState(null);
  const currentFolderIdRef = useRef(null);
  const [newFolderModal, setNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderSaving, setNewFolderSaving] = useState(false);
  const [renameFolderModal, setRenameFolderModal] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [deleteFolderConfirm, setDeleteFolderConfirm] = useState(null);
  const [deleteFolderDeleteFiles, setDeleteFolderDeleteFiles] = useState(false);
  const [moveFileModal, setMoveFileModal] = useState(null);

  // Load data on mount
  useEffect(() => {
    loadFiles();
    loadFolders();
    loadUsage();
    checkOwnerStatus();
    loadPendingInvCount();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync with parent's activeTeamContext:
  // - On page reload: teamContext is set from sessionStorage but teamFiles is empty → load files
  // - On async URL load: activeTeamContext arrives after mount → Dashboard has stale null teamContext
  useEffect(() => {
    if (!activeTeamContext?.teamSpaceId || !token) return;
    const sameSpace = teamContext?.teamSpaceId === activeTeamContext.teamSpaceId;
    if (sameSpace && teamFiles.length > 0) return; // already loaded, nothing to do
    switchToTeam(activeTeamContext);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeamContext?.teamSpaceId, token]);

  // Auto-refresh when files are processing
  useEffect(() => {
    const hasProcessing = files.some(f => f.processing_status && f.processing_status !== 'ready' && f.processing_status !== 'failed');
    if (!hasProcessing) return;
    const interval = setInterval(() => loadFiles(), 3000);
    return () => clearInterval(interval);
  }, [files]);

  // Check if user is owner
  const checkOwnerStatus = async () => {
    try {
      const response = await fetch('/api/admin/is-owner', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIsOwner(data.is_owner);
      }
    } catch (error) {
      console.error('Owner check error:', error);
    }
  };

  // Load pending invitation count (for badge)
  const loadPendingInvCount = async () => {
    try {
      const res = await fetch('/api/team/pending-count', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setPendingInvCount(d.count || 0); }
    } catch (_) {}
  };

  // Kicked out of team — show message and go back to own storage
  const kickedFromTeam = (reason) => {
    setTeamContext(null);
    setTeamFiles([]);
    showToast(reason || 'Zugriff auf Team-Speicher wurde widerrufen.', 'error');
  };

  // Switch to team storage — verifies access before switching
  const switchToTeam = async (ctx) => {
    setLoadingTeamFiles(true);
    try {
      const res = await fetch(`/api/team/spaces/${ctx.teamSpaceId}/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 403 || res.status === 404) {
        const d = await res.json().catch(() => ({}));
        showToast(d.error || 'Kein Zugriff auf diesen Team-Speicher.', 'error');
        setLoadingTeamFiles(false);
        return; // do NOT switch context
      }
      if (res.ok) {
        const d = await res.json();
        setTeamFiles(d.files || []);
        setTeamContext(ctx); // only set context after confirmed access
        if (onTeamContextChange) onTeamContextChange(ctx);
      }
    } catch (_) {
      showToast('Team-Speicher konnte nicht geladen werden.', 'error');
    } finally {
      setLoadingTeamFiles(false);
    }
  };

  const switchToOwnStorage = () => {
    try { sessionStorage.removeItem('avs_team_context'); } catch {}
    window.location.href = '/';
  };


  // Re-validate team access (called periodically + on window focus)
  const revalidateTeamAccess = useCallback(async () => {
    if (!teamContext) return;
    try {
      const res = await fetch(`/api/team/spaces/${teamContext.teamSpaceId}/access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 403 || res.status === 404) {
        const d = await res.json().catch(() => ({}));
        kickedFromTeam(d.error || 'Zugriff auf Team-Speicher wurde widerrufen.');
      }
    } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamContext, token]);

  // Re-validate team access every 60s + on window focus
  // (placed here, AFTER revalidateTeamAccess is declared)
  useEffect(() => {
    if (!teamContext) return;
    const interval = setInterval(revalidateTeamAccess, 60 * 1000);
    window.addEventListener('focus', revalidateTeamAccess);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', revalidateTeamAccess);
    };
  }, [teamContext, revalidateTeamAccess]);

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const loadFiles = useCallback(async (page = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoadingFiles(true);
    }
    
    try {
      const activeFolderId = currentFolderIdRef.current;
      const folderQ = activeFolderId ? `&folder_id=${encodeURIComponent(activeFolderId)}` : '&folder_id=null';
      const response = await fetch(`/api/files/my-files?page=${page}&limit=${ITEMS_PER_PAGE}${folderQ}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to load files');
      
      const data = await response.json();
      
      if (append) {
        setFiles(prev => [...prev, ...(data.files || [])]);
      } else {
        setFiles(data.files || []);
      }
      
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalFiles(data.total || 0);
      setCurrentPage(page);
      
    } catch (error) {
      console.error('Load files error:', error);
      showToast('Fehler beim Laden der Dateien');
    } finally {
      setLoadingFiles(false);
      setLoadingMore(false);
    }
  }, [token, ITEMS_PER_PAGE]);

  const loadMore = () => {
    if (currentPage < totalPages && !loadingMore) {
      loadFiles(currentPage + 1, true);
    }
  };

  const loadFolders = async () => {
    if (teamContext) return;
    try {
      const res = await fetch('/api/folders', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setFolders(d.folders || []); }
    } catch (_) {}
  };

  const navigateToFolder = (folderId, folderName) => {
    currentFolderIdRef.current = folderId;
    setCurrentFolderId(folderId);
    setCurrentFolderName(folderName);
    if (onFolderContextChange) onFolderContextChange(folderId, folderName);
    setFiles([]);
    setCurrentPage(1);
    setSelectedFileIds([]);
    loadFiles(1, false);
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    setNewFolderSaving(true);
    try {
      const res = await fetch('/api/folders', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const d = await res.json();
      if (d.success) {
        setFolders(prev => [...prev, d.folder].sort((a, b) => a.name.localeCompare(b.name)));
        setNewFolderModal(false);
        setNewFolderName('');
        showToast('Ordner erstellt', 'success');
      } else {
        showToast(d.error || 'Fehler beim Erstellen');
      }
    } catch (_) { showToast('Verbindungsfehler'); }
    setNewFolderSaving(false);
  };

  const handleRenameFolder = async () => {
    const name = renameFolderName.trim();
    if (!name || !renameFolderModal) return;
    try {
      const res = await fetch(`/api/folders/${renameFolderModal.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const d = await res.json();
      if (d.success) {
        setFolders(prev => prev.map(f => f.id === renameFolderModal.id ? { ...f, name: d.name } : f).sort((a, b) => a.name.localeCompare(b.name)));
        if (currentFolderId === renameFolderModal.id) setCurrentFolderName(d.name);
        setRenameFolderModal(null);
        showToast('Ordner umbenannt', 'success');
      } else {
        showToast(d.error || 'Fehler beim Umbenennen');
      }
    } catch (_) { showToast('Verbindungsfehler'); }
  };

  const handleDeleteFolder = async (folder, deleteFiles) => {
    try {
      const res = await fetch(`/api/folders/${folder.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ delete_files: deleteFiles }),
      });
      const d = await res.json();
      if (d.success) {
        setFolders(prev => prev.filter(f => f.id !== folder.id));
        if (currentFolderId === folder.id) {
          navigateToFolder(null, null);
        } else {
          loadFiles(1, false);
          loadUsage();
        }
        setDeleteFolderConfirm(null);
        setDeleteFolderDeleteFiles(false);
        showToast(deleteFiles ? 'Ordner und Dateien gelöscht' : 'Ordner gelöscht', 'success');
      } else {
        showToast(d.error || 'Fehler beim Löschen');
      }
    } catch (_) { showToast('Verbindungsfehler'); }
  };

  const handleMoveFiles = async (fileIds, targetFolderId) => {
    try {
      const endpoint = targetFolderId
        ? `/api/folders/${targetFolderId}/move-files`
        : '/api/folders/root/move-files';
      const res = await fetch(endpoint, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileIds }),
      });
      const d = await res.json();
      if (d.success) {
        setFiles(prev => prev.filter(f => !fileIds.includes(f.id)));
        setSelectedFileIds(prev => prev.filter(id => !fileIds.includes(id)));
        await loadFolders();
        setMoveFileModal(null);
        showToast('Datei(en) verschoben', 'success');
      } else {
        showToast(d.error || 'Fehler beim Verschieben');
      }
    } catch (_) { showToast('Verbindungsfehler'); }
  };

  const loadUsage = useCallback(async () => {
    try {
      const response = await fetch('/api/files/usage', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to load usage');
      
      const data = await response.json();
      setUsage(data);
    } catch (error) {
      console.error('Load usage error:', error);
    }
  }, [token]);

  // ============================================================================
  // FILE ACTIONS
  // ============================================================================
  
  const handleDownload = async (fileId, filename) => {
    // Check if file is still processing
    const fileList = teamContext ? teamFiles : files;
    const processingFile = fileList.find(f => f.id === fileId);
    if (processingFile && processingFile.processing_status && processingFile.processing_status !== 'ready') {
      showToast('Datei wird noch verarbeitet. Bitte warte einen Moment.');
      return;
    }

    showToast('Download wird vorbereitet...', 'info');
    setActiveDownloads(prev => ({...prev, [fileId]: { filename, progress: 0 }}));

    const downloadUrl = teamContext
      ? `/api/team/spaces/${teamContext.teamSpaceId}/download/${fileId}`
      : `/api/files/download/${fileId}`;

    try {
      const response = await fetch(downloadUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        setActiveDownloads(prev => { const n = {...prev}; delete n[fileId]; return n; });
        if (response.status === 403 && teamContext) {
          kickedFromTeam(errData.error || 'Zugriff auf Team-Speicher wurde widerrufen.');
          return;
        }
        if (errData.code === 'FILE_PROCESSING') {
          showToast('Datei wird noch verarbeitet (Virenscan & Verschlüsselung).');
          return;
        }
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setActiveDownloads(prev => { const n = {...prev}; delete n[fileId]; return n; });
    } catch (error) {
      console.error('Download error:', error);
      setActiveDownloads(prev => { const n = {...prev}; delete n[fileId]; return n; });
      showToast('Download fehlgeschlagen');
    }
  };

  const handleDelete = async (fileId) => {
    try {
      const url = teamContext
        ? `/api/team/spaces/${teamContext.teamSpaceId}/files/${fileId}`
        : `/api/files/delete/${fileId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Delete failed');

      if (teamContext) {
        // Reload team files
        const res = await fetch(`/api/team/spaces/${teamContext.teamSpaceId}/files`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) { const d = await res.json(); setTeamFiles(d.files || []); }
      } else {
        await loadFiles();
        await loadUsage();
      }
      setDeleteConfirmFile(null);
    } catch (error) {
      console.error('Delete error:', error);
      showToast('Löschen fehlgeschlagen');
    }
  };

  // ============================================================================
  // BULK DELETE FUNCTIONS
  // ============================================================================
  
  const toggleFileSelection = (fileId) => {
    setSelectedFileIds(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedFileIds.length === displayedFiles.length) {
      setSelectedFileIds([]);
    } else {
      setSelectedFileIds(displayedFiles.map(f => f.id));
    }
  };

  const handleBulkDownload = async () => {
    if (selectedFileIds.length === 0) return;

    setBulkDownloading(true);

    try {
      // Step 1: get a one-time download token (fast, authenticated)
      const response = await fetch('/api/files/bulk-download', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fileIds: selectedFileIds })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Download fehlgeschlagen');

      // Step 2: let the browser stream the ZIP natively — no JS memory buffering
      window.location.href = `/api/files/bulk-download/${data.downloadToken}`;

      setSelectedFileIds([]);

    } catch (error) {
      console.error('Bulk download error:', error);
      showToast('Download fehlgeschlagen: ' + error.message);
    } finally {
      setBulkDownloading(false);
    }
  };

  // ============================================================================
  // ABUSE REPORT (für Share-Link Seiten)
  // ============================================================================

  const handleAbuseSubmit = async () => {
    if (!abuseType) return;
    setAbuseSubmitting(true);
    try {
      const res = await fetch('/api/abuse/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          share_token: abuseReportModal?.share_token,
          report_type: abuseType,
          description: abuseDescription.trim() || undefined,
          reporter_email: abuseEmail.trim() || undefined,
        }),
      });
      if (res.ok) {
        setAbuseSuccess(true);
      }
    } catch (err) {
      console.error('[ABUSE] Submit error:', err);
    } finally {
      setAbuseSubmitting(false);
    }
  };

  const closeAbuseModal = () => {
    setAbuseReportModal(null);
    setAbuseType('');
    setAbuseDescription('');
    setAbuseEmail('');
    setAbuseSuccess(false);
    setAbuseSubmitting(false);
  };

  const handleBulkDelete = async () => {
    try {
      let successCount = 0;
      let failCount = 0;

      for (const fileId of selectedFileIds) {
        try {
          const response = await fetch(`/api/files/delete/${fileId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
        }
      }

      // Reload data
      await loadFiles();
      await loadUsage();
      setShowBulkDeleteConfirm(false);
      setSelectedFileIds([]);

      if (failCount > 0) {
        showToast(`${successCount} Dateien gelöscht, ${failCount} fehlgeschlagen`, successCount > 0 ? 'success' : 'error');
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      showToast('Fehler beim Löschen');
    }
  };

  const handleShare = async (fileId) => {
    // ShareLinkModal öffnet sich automatisch via shareModalFile State
  };

  // ============================================================================
  // FILE INFO & METADATA
  // ============================================================================
  
  const handleInfo = async (file) => {
    setInfoModalFile(file);
    setFileMetadata(null);
    setLoadingMetadata(true);

    try {
      const response = await fetch(`/api/files/metadata/${file.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setFileMetadata(data.file.metadata);
      } else {
        console.error('Failed to fetch metadata');
      }
    } catch (error) {
      console.error('Metadata fetch error:', error);
    } finally {
      setLoadingMetadata(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unbekannt';
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatExposure = (exposure) => {
    if (!exposure) return null;
    if (exposure < 1) {
      return `1/${Math.round(1 / exposure)}s`;
    }
    return `${exposure.toFixed(1)}s`;
  };

  // ============================================================================
  // FILTERING & SORTING
  // ============================================================================
  
  const filteredAndSortedFiles = useCallback(() => {
    let result = [...files];

    // Folder filter
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    switch (activeFolder) {
      case 'today':
        result = result.filter(file => new Date(file.uploaded_at) >= today);
        break;
      case 'week':
        result = result.filter(file => new Date(file.uploaded_at) >= weekAgo);
        break;
      case 'month':
        result = result.filter(file => new Date(file.uploaded_at) >= monthStart);
        break;
      case 'images':
        result = result.filter(file => /^image\//i.test(file.mime_type));
        break;
      case 'videos':
        result = result.filter(file => /^video\//i.test(file.mime_type));
        break;
      case 'documents':
        result = result.filter(file => 
          /^(application\/pdf|application\/msword|application\/vnd\.|text\/)/i.test(file.mime_type)
        );
        break;
      case 'all':
      default:
        break;
    }

    // Search filter
    if (searchQuery) {
      result = result.filter(file =>
        file.filename.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.filename.localeCompare(b.filename);
          break;
        case 'size':
          comparison = a.file_size - b.file_size;
          break;
        case 'date':
        default:
          comparison = new Date(a.uploaded_at) - new Date(b.uploaded_at);
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [files, searchQuery, sortBy, sortOrder, activeFolder]);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================
  
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
	    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };


   const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    
    const iconMap = {
      // Images
      jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️', webp: '🖼️', svg: '🖼️',
      // Videos
      mp4: '🎬', mov: '🎬', avi: '🎬', mkv: '🎬', webm: '🎬',
      // Audio
      mp3: '🎵', wav: '🎵', flac: '🎵', m4a: '🎵',
      // Documents
      pdf: '📄', doc: '📝', docx: '📝', txt: '📝',
      xls: '📊', xlsx: '📊', csv: '📊',
      ppt: '📊', pptx: '📊',
      // Archives
      zip: '📦', rar: '📦', '7z': '📦', tar: '📦', gz: '📦',
      // Code
      js: '⚙️', jsx: '⚙️', ts: '⚙️', tsx: '⚙️',
      py: '🐍', java: '☕', cpp: '⚙️', c: '⚙️',
      html: '🌐', css: '🎨', json: '📋',
    };

    return iconMap[ext] || '📁';
  };

// Check if file is image
const isImage = (filename) => {
  const ext = filename.toLowerCase().split('.').pop();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
};

const isVideo = (filename) => {
  const ext = filename.toLowerCase().split('.').pop();
  return ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'm4v'].includes(ext);
};

const isAudio = (filename) => {
  const ext = filename.toLowerCase().split('.').pop();
  return ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'wma', 'opus'].includes(ext);
};

// Load image with caching
const loadImage = useCallback(async (fileId) => {
  if (imageCache[fileId]) return imageCache[fileId];

  const dlUrl = teamContext
    ? `/api/team/spaces/${teamContext.teamSpaceId}/download/${fileId}`
    : `/api/files/download/${fileId}`;

  try {
    const response = await fetch(dlUrl, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      if (response.status === 403 && teamContext) {
        const d = await response.json().catch(() => ({}));
        kickedFromTeam(d.error);
      }
      return null;
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    setImageCache(prev => ({ ...prev, [fileId]: url }));
    return url;
  } catch (error) {
    console.error('Image load error:', error);
    return null;
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [token, imageCache, teamContext]);

// Load video
const loadVideo = async (file) => {
  setVideoPlayerModal(file);
  setLoadingVideo(true);
  setVideoUrl(null);

  const dlUrl = teamContext
    ? `/api/team/spaces/${teamContext.teamSpaceId}/download/${file.id}`
    : `/api/files/download/${file.id}`;

  try {
    const response = await fetch(dlUrl, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      if (response.status === 403 && teamContext) {
        const d = await response.json().catch(() => ({}));
        kickedFromTeam(d.error);
        setVideoPlayerModal(null);
        return;
      }
      throw new Error('Video load failed');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    setVideoUrl(url);
  } catch (error) {
    console.error('Video load error:', error);
    showToast('Video konnte nicht geladen werden');
    setVideoPlayerModal(null);
  } finally {
    setLoadingVideo(false);
  }
};

const closeVideoPlayer = () => {
  if (videoUrl) {
    URL.revokeObjectURL(videoUrl);
  }
  setVideoPlayerModal(null);
  setVideoUrl(null);
};

// Load audio
const loadAudio = async (file) => {
  setAudioPlayerModal(file);
  setLoadingAudio(true);
  setAudioUrl(null);

  const dlUrl = teamContext
    ? `/api/team/spaces/${teamContext.teamSpaceId}/download/${file.id}`
    : `/api/files/download/${file.id}`;

  try {
    const response = await fetch(dlUrl, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      if (response.status === 403 && teamContext) {
        const d = await response.json().catch(() => ({}));
        kickedFromTeam(d.error);
        setAudioPlayerModal(null);
        return;
      }
      throw new Error('Audio load failed');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    setAudioUrl(url);
  } catch (error) {
    console.error('Audio load error:', error);
    showToast('Audio konnte nicht geladen werden');
    setAudioPlayerModal(null);
  } finally {
    setLoadingAudio(false);
  }
};

const closeAudioPlayer = () => {
  if (audioUrl) {
    URL.revokeObjectURL(audioUrl);
  }
  setAudioPlayerModal(null);
  setAudioUrl(null);
};

// Auto-load thumbnails
  const storagePercentage = usage.total_storage && usage.storage_limit
    ? (usage.total_storage / usage.storage_limit) * 100
    : 0;

  // Team-Quota: used_bytes live aus geladenen Dateien berechnen (immer aktuell)
  const teamUsedBytes = teamFiles.reduce((sum, f) => sum + (f.file_size || 0), 0);
  const teamQuotaBytes = teamContext?.quotaBytes || 0;
  const teamStoragePercentage = teamContext?.isSeparate && teamQuotaBytes > 0
    ? (teamUsedBytes / teamQuotaBytes) * 100
    : 0;

  // In Team-Kontext: Team-Dateien anzeigen, sonst eigene gefilterte Dateien
  const displayedFiles = teamContext ? teamFiles : filteredAndSortedFiles();

  // Get folder counts
  const getFolderCounts = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    return {
      all: files.length,
      today: files.filter(f => new Date(f.uploaded_at) >= today).length,
      week: files.filter(f => new Date(f.uploaded_at) >= weekAgo).length,
      month: files.filter(f => new Date(f.uploaded_at) >= monthStart).length,
      images: files.filter(f => /^image\//i.test(f.mime_type)).length,
      videos: files.filter(f => /^video\//i.test(f.mime_type)).length,
      documents: files.filter(f => /^(application\/pdf|application\/msword|application\/vnd\.|text\/)/i.test(f.mime_type)).length
    };
  };

  const folderCounts = getFolderCounts();

  // ============================================================================
  // RENDER
  // ============================================================================
  
  // Rename handler
  const handleRename = async () => {
    if (!renameModal || !renameValue.trim()) return;
    try {
      const res = await fetch(`/api/files/rename/${renameModal.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: renameValue.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setFiles(prev => prev.map(f => f.id === renameModal.id ? { ...f, filename: data.filename } : f));
        showToast('Datei umbenannt', 'success');
      } else {
        showToast(data.error || 'Umbenennen fehlgeschlagen');
      }
    } catch (err) {
      showToast('Umbenennen fehlgeschlagen: ' + err.message);
    }
    setRenameModal(null);
  };

  return (
    <div style={{minHeight: '100vh', paddingBottom: '5rem', fontFamily: 'var(--font-head)', color: 'var(--white)'}}>

      
      {/* Toast Notification */}
      {toast && (
        <div onClick={() => setToast(null)} style={{
          position: 'fixed', top: '1rem', left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, maxWidth: '500px', width: '90%', padding: '0.85rem 1.25rem',
          background: toast.type === 'success' ? 'rgba(74,222,128,0.15)' : 'rgba(255,80,80,0.15)',
          border: `1px solid ${toast.type === 'success' ? 'rgba(74,222,128,0.3)' : 'rgba(255,80,80,0.3)'}`,
          borderRadius: '10px', color: toast.type === 'success' ? '#4ade80' : '#ff8888',
          fontSize: '0.85rem', cursor: 'pointer', backdropFilter: 'blur(12px)',
          textAlign: 'center', fontWeight: 600,
        }}>{toast.message}</div>
      )}

      {/* Rename Modal */}
      {renameModal && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem'}} onClick={() => setRenameModal(null)}>
          <BodyScrollLock />
          <div className="avs-card" style={{maxWidth: '440px', width: '100%', padding: '1.75rem', maxHeight: '85vh', overflowY: 'auto'}} onClick={e => e.stopPropagation()}>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem'}}>
              <div style={{width: '38px', height: '38px', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 'var(--r-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem'}}>✎</div>
              <div>
                <h3 style={{fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '1rem', color: 'var(--white)'}}>Datei umbenennen</h3>
                <p style={{fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--muted)', letterSpacing: '0.08em', textTransform: 'uppercase'}}>Name ändern</p>
              </div>
            </div>
            <input
              type="text"
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleRename()}
              autoFocus
              style={{width: '100%', padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', color: 'var(--white)', fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'var(--font-mono)'}}
            />
            <div style={{display: 'flex', gap: '0.75rem', marginTop: '1.25rem'}}>
              <button onClick={() => setRenameModal(null)} className="avs-btn-ghost" style={{flex: 1, padding: '0.75rem', justifyContent: 'center'}}>Abbrechen</button>
              <button onClick={handleRename} className="avs-btn" style={{flex: 1, padding: '0.75rem', justifyContent: 'center'}}>Umbenennen</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────── */}
      <header style={{
        borderBottom: '1px solid var(--border)',
        background: 'rgba(4, 10, 20, 0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        <div style={{maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem'}}>
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px', gap: '1rem'}}>
            {/* Logo + Wordmark */}
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
              <img src="/avs-logo.png" alt="AquaV" className="avs-logo-img" />
              <div className="avs-wordmark">
                Aqua<span className="av">V</span>
                <span className="sub">Cloud</span>
              </div>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6rem'}}>
              {/* Team Button */}
              <button onClick={() => setShowTeamManager(true)} style={{
                position: 'relative', display: 'flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.45rem 0.85rem', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 700,
                fontFamily: 'var(--font-head)', cursor: 'pointer', transition: 'all 0.2s',
                background: teamContext ? 'rgba(168,85,247,0.15)' : 'rgba(168,85,247,0.08)',
                border: `1px solid ${teamContext ? 'rgba(168,85,247,0.5)' : 'rgba(168,85,247,0.2)'}`,
                color: '#a855f7',
              }}>
                <span>👥</span>
                <span style={{display: 'none'}} className="sm-inline">Team</span>
                {pendingInvCount > 0 && (
                  <span style={{position: 'absolute', top: '-5px', right: '-5px', background: '#ef4444', color: '#fff', borderRadius: '50%', width: '16px', height: '16px', fontSize: '0.6rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1}}>{pendingInvCount}</span>
                )}
              </button>
              <UserAvatar token={token} onLogout={onLogout} />
            </div>
          </div>
        </div>
      </header>

      {/* ── Over-Limit Banner ────────────────────────────── */}
      {!teamContext && usage.total_storage > 0 && usage.storage_limit > 0 && usage.total_storage > usage.storage_limit && (
        <div style={{background: 'linear-gradient(135deg, rgba(239,68,68,0.18), rgba(185,28,28,0.10))', borderBottom: '1px solid rgba(239,68,68,0.35)', padding: '0.6rem 1.5rem'}}>
          <div style={{maxWidth: '1280px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6rem'}}>
              <span style={{fontSize: '1rem'}}>🚨</span>
              <span style={{fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#ef4444'}}>Speicherlimit überschritten</span>
              <span style={{fontSize: '0.82rem', color: '#fca5a5'}}>
                Du belegst <strong>{formatBytes(usage.total_storage)}</strong> von <strong>{formatBytes(usage.storage_limit)}</strong> — neue Uploads sind gesperrt.
              </span>
            </div>
            <span style={{fontSize: '0.78rem', color: '#f87171', fontWeight: 600}}>
              Lösche Dateien oder erweitere deinen Speicher.
            </span>
          </div>
        </div>
      )}

      {/* ── Team-Speicher Banner ──────────────────────────── */}
      {teamContext && (
        <div style={{background: 'linear-gradient(135deg, rgba(168,85,247,0.15), rgba(124,58,237,0.08))', borderBottom: '1px solid rgba(168,85,247,0.25)', padding: '0.6rem 1.5rem'}}>
          <div style={{maxWidth: '1280px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6rem'}}>
              <span style={{fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#a855f7'}}>👥 Team-Speicher</span>
              <span style={{fontSize: '0.82rem', color: '#dceeff', fontWeight: 600}}>{teamContext.spaceName || teamContext.ownerUsername}</span>
              {!teamContext.isOwner && <span style={{fontSize: '0.72rem', color: '#8892a4'}}>({teamContext.ownerUsername})</span>}
              <span style={{fontSize: '0.68rem', background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)', borderRadius: '20px', padding: '2px 8px', color: '#c084fc', fontWeight: 700}}>
                {{ read: 'Lesen', write: 'Upload', read_write: 'Voll-Zugriff' }[teamContext.permissions]}
              </span>
            </div>
            <button onClick={switchToOwnStorage} style={{fontSize: '0.75rem', fontWeight: 700, color: '#a855f7', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem 0.5rem'}}>← Eigener Speicher</button>
          </div>
        </div>
      )}

      {/* ── Team Over-Limit Banner ────────────────────────── */}
      {teamContext && teamContext.isSeparate && teamQuotaBytes > 0 && teamUsedBytes > teamQuotaBytes && (
        <div style={{background: 'linear-gradient(135deg, rgba(239,68,68,0.18), rgba(185,28,28,0.10))', borderBottom: '1px solid rgba(239,68,68,0.35)', padding: '0.6rem 1.5rem'}}>
          <div style={{maxWidth: '1280px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6rem'}}>
              <span style={{fontSize: '1rem'}}>🚨</span>
              <span style={{fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#ef4444'}}>Team-Quota überschritten</span>
              <span style={{fontSize: '0.82rem', color: '#fca5a5'}}>
                <strong>{formatBytes(teamUsedBytes)}</strong> von <strong>{formatBytes(teamQuotaBytes)}</strong> — keine weiteren Uploads möglich.
              </span>
            </div>
            {teamContext.isOwner && (
              <span style={{fontSize: '0.78rem', color: '#f87171', fontWeight: 600}}>Quota erhöhen oder Dateien löschen.</span>
            )}
          </div>
        </div>
      )}

      {/* ── Storage Overview Card ──────────────────────────── */}
      <div style={{maxWidth: '1280px', margin: '0 auto', padding: '1.5rem 1.5rem 0'}}>
        <div className="avs-card" style={{padding: 'clamp(1.25rem, 3vw, 2rem)'}}>
          {teamContext ? (
            /* Team-Speicher Stats */
            <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem'}}>
              <div style={{flex: '1', minWidth: '240px'}}>
                <p className="avs-label" style={{marginBottom: '1rem', color: '#a855f7'}}>👥 {teamContext.spaceName}</p>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem'}}>
                  {[
                    {val: teamFiles.length, label: 'Dateien'},
                    ...(teamContext.isSeparate && teamQuotaBytes > 0
                      ? [{val: formatBytes(teamUsedBytes), label: 'Verwendet', over: teamUsedBytes > teamQuotaBytes},
                         {val: formatBytes(teamQuotaBytes), label: 'Quota'}]
                      : []
                    ),
                  ].map(({val, label, over}) => (
                    <div key={label}>
                      <div style={{fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1.6rem', letterSpacing: '-0.03em', color: over ? '#ef4444' : 'var(--white)'}}>{val}</div>
                      <div style={{fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: '2px'}}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{flex: '1', minWidth: '200px', maxWidth: '380px'}}>
                <div style={{fontSize: '0.78rem', color: '#8892a4', marginBottom: teamContext.isSeparate && teamQuotaBytes > 0 ? '0.75rem' : 0}}>
                  {teamContext.isOwner ? 'Eigener Team-Speicher' : `Inhaber: ${teamContext.ownerUsername}`}
                  {teamContext.isSeparate && <span style={{display: 'block', marginTop: '0.25rem', color: '#a855f7', fontWeight: 600}}>Separater Speicher</span>}
                </div>
                {teamContext.isSeparate && teamQuotaBytes > 0 && (
                  <>
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem'}}>
                      <span style={{fontSize: '0.7rem', color: 'var(--muted)', letterSpacing: '0.06em', textTransform: 'uppercase'}}>Quota-Nutzung</span>
                      <span style={{fontSize: '0.7rem', fontWeight: 700, color: teamStoragePercentage > 100 ? '#ef4444' : teamStoragePercentage > 90 ? '#f87171' : teamStoragePercentage > 80 ? '#f59e0b' : '#a855f7'}}>
                        {teamStoragePercentage.toFixed(1)}%
                      </span>
                    </div>
                    <div style={{height: '6px', background: 'rgba(168,85,247,0.1)', borderRadius: '3px', overflow: 'hidden'}}>
                      <div style={{
                        height: '100%',
                        width: `${Math.min(teamStoragePercentage, 100)}%`,
                        background: teamStoragePercentage > 100
                          ? 'linear-gradient(90deg, #dc2626, #991b1b)'
                          : teamStoragePercentage > 90
                          ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                          : teamStoragePercentage > 80
                          ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                          : 'linear-gradient(90deg, #a855f7, #7c3aed)',
                        borderRadius: '3px',
                        transition: 'width 0.5s ease',
                        boxShadow: teamStoragePercentage > 100 ? '0 0 8px rgba(220,38,38,0.6)' : teamStoragePercentage > 80 ? '0 0 8px rgba(239,68,68,0.4)' : '0 0 8px rgba(168,85,247,0.4)',
                        animation: teamStoragePercentage > 100 ? 'avs-pulse-red 1.5s ease-in-out infinite' : 'none'
                      }} />
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            /* Private Storage Stats */
            <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem'}}>
              <div style={{flex: '1', minWidth: '240px'}}>
                <p className="avs-label" style={{marginBottom: '1rem'}}>Deine Cloud</p>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem'}}>
                  {[
                    {val: usage.total_files, label: 'Dateien'},
                    {val: formatBytes(usage.total_storage), label: 'Verwendet'},
                    {val: formatBytes(usage.storage_limit), label: 'Limit'}
                  ].map(({val, label}) => (
                    <div key={label}>
                      <div style={{fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1.6rem', letterSpacing: '-0.03em', color: 'var(--white)'}}>{val}</div>
                      <div style={{fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: '2px'}}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{flex: '1', minWidth: '200px', maxWidth: '380px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem'}}>
                  <span style={{fontSize: '0.75rem', color: 'var(--muted)', letterSpacing: '0.06em', textTransform: 'uppercase'}}>Speichernutzung</span>
                  <span style={{fontSize: '0.75rem', fontWeight: 700, color: storagePercentage > 100 ? '#ef4444' : storagePercentage > 90 ? '#f87171' : storagePercentage > 80 ? '#f59e0b' : 'var(--cyan)'}}>{storagePercentage.toFixed(1)}%</span>
                </div>
                <div style={{height: '6px', background: 'rgba(18,112,196,0.1)', borderRadius: '3px', overflow: 'hidden'}}>
                  <div style={{
                    height: '100%',
                    width: `${Math.min(storagePercentage, 100)}%`,
                    background: storagePercentage > 100
                      ? 'linear-gradient(90deg, #dc2626, #991b1b)'
                      : storagePercentage > 90
                      ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                      : storagePercentage > 80
                      ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                      : 'linear-gradient(90deg, var(--cyan), var(--blue-mid))',
                    borderRadius: '3px',
                    transition: 'width 0.5s ease',
                    boxShadow: storagePercentage > 100 ? '0 0 8px rgba(220,38,38,0.6)' : storagePercentage > 90 ? '0 0 8px rgba(239,68,68,0.5)' : storagePercentage > 80 ? '0 0 8px rgba(245,158,11,0.4)' : '0 0 8px rgba(26,180,240,0.4)',
                    position: 'relative',
                    overflow: 'hidden',
                    animation: storagePercentage > 100 ? 'avs-pulse-red 1.5s ease-in-out infinite' : 'none'
                  }}>
                    <div className="avs-shimmer" style={{position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)'}}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Controls Bar ──────────────────────────────────── */}
      <div style={{maxWidth: '1280px', margin: '0 auto', padding: '1rem 1.5rem'}}>
        <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
          {/* Row 1: Folder + Search */}
          <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center'}}>
            {/* Folder filter nur im eigenen Speicher */}
            <select
              value={activeFolder}
              onChange={(e) => setActiveFolder(e.target.value)}
              className="avs-input"
              style={{width: 'auto', cursor: 'pointer', display: teamContext ? 'none' : undefined}}
            >
              <option value="all">📂 Alle ({folderCounts.all})</option>
              <option value="today">📅 Heute ({folderCounts.today})</option>
              <option value="week">📅 Diese Woche ({folderCounts.week})</option>
              <option value="month">📅 Dieser Monat ({folderCounts.month})</option>
              <option value="images">🖼️ Bilder ({folderCounts.images})</option>
              <option value="videos">🎬 Videos ({folderCounts.videos})</option>
              <option value="documents">📄 Dokumente ({folderCounts.documents})</option>
            </select>

            <div style={{position: 'relative', flex: '1', maxWidth: '400px', minWidth: '180px'}}>
              <svg style={{position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', width: '16px', height: '16px'}} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Dateien suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="avs-input"
                style={{paddingLeft: '2.75rem'}}
              />
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedFileIds.length > 0 && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem',
              padding: '0.875rem 1rem',
              background: 'rgba(18, 112, 196, 0.06)',
              border: '1px solid var(--border-hov)',
              borderRadius: 'var(--r-lg)'
            }}>
              <span style={{fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--cyan)', letterSpacing: '0.08em', flex: 1}}>
                {selectedFileIds.length} AUSGEWÄHLT
              </span>
              <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                <button onClick={handleBulkDownload} disabled={bulkDownloading} className="avs-btn" style={{padding: '0.5rem 1rem', fontSize: '0.8rem', opacity: bulkDownloading ? 0.5 : 1}}>
                  {bulkDownloading ? 'Erstelle ZIP...' : '↓ Download'}
                </button>
                {!teamContext && (
                  <button onClick={() => setMoveFileModal('bulk')} style={{padding: '0.5rem 1rem', fontSize: '0.8rem', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 'var(--r-md)', color: '#34d399', cursor: 'pointer', fontFamily: 'var(--font-head)', fontWeight: 600}}>
                    ⇲ Verschieben
                  </button>
                )}
                <button onClick={() => setShowBulkDeleteConfirm(true)} style={{padding: '0.5rem 1rem', fontSize: '0.8rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--r-md)', color: '#f87171', cursor: 'pointer', fontFamily: 'var(--font-head)', fontWeight: 600}}>
                  🗑 Löschen
                </button>
                <button onClick={() => setSelectedFileIds([])} className="avs-btn-ghost" style={{padding: '0.5rem 1rem', fontSize: '0.8rem'}}>
                  Abbrechen
                </button>
              </div>
            </div>
          )}

          {/* Row 2: Actions */}
          <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center'}}>
            {/* Select All */}
            <button
              onClick={toggleSelectAll}
              className="avs-btn-ghost"
              style={{
                padding: '0.6rem 1rem', fontSize: '0.8rem',
                background: selectedFileIds.length === displayedFiles.length && displayedFiles.length > 0 ? 'rgba(18,112,196,0.12)' : undefined,
                borderColor: selectedFileIds.length === displayedFiles.length && displayedFiles.length > 0 ? 'var(--border-hov)' : undefined,
                color: selectedFileIds.length === displayedFiles.length && displayedFiles.length > 0 ? 'var(--cyan)' : undefined
              }}
            >
              {selectedFileIds.length === displayedFiles.length && displayedFiles.length > 0 ? '☑ Alle' : '☐ Alle'}
            </button>

            {/* Sort By */}
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="avs-input" style={{width: 'auto', fontSize: '0.82rem', padding: '0.6rem 0.75rem', cursor: 'pointer'}}>
              <option value="date">Datum</option>
              <option value="name">Name</option>
              <option value="size">Größe</option>
            </select>

            {/* Sort Order */}
            <button onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')} className="avs-btn-ghost" style={{padding: '0.6rem 0.75rem', fontSize: '0.85rem'}}>
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>

            {/* View Mode */}
            <button onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')} className="avs-btn-ghost" style={{padding: '0.6rem 0.75rem', fontSize: '0.85rem'}}>
              {viewMode === 'grid' ? '≡' : '⊞'}
            </button>

            {/* Share Links */}
            <button onClick={() => setShowShareLinksManager(true)} className="avs-btn-ghost" style={{padding: '0.6rem 1rem', fontSize: '0.8rem'}}>
              🔗 Links
            </button>

            {/* Admin */}
            {isOwner && (
              <button onClick={() => setShowAdminPanel(true)} className="avs-btn" style={{padding: '0.6rem 1rem', fontSize: '0.8rem'}}>
                ⚙ Admin
              </button>
            )}

            {/* Upload */}
            <button
              onClick={onBackToUpload}
              className="avs-btn"
              style={{padding: '0.6rem 1.25rem', fontSize: '0.82rem', marginLeft: 'auto'}}
            >
              ↑ Upload
            </button>
          </div>
        </div>
      </div>

      {/* Folder Area — only personal storage, no search active */}
      {!teamContext && !searchQuery && (
        <div style={{maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem 0.5rem'}}>
          {currentFolderId ? (
            /* Breadcrumb */
            <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0'}}>
              <button
                onClick={() => navigateToFolder(null, null)}
                style={{background: 'none', border: 'none', color: 'var(--cyan)', cursor: 'pointer', fontFamily: 'var(--font-head)', fontWeight: 600, fontSize: '0.85rem', padding: '0.3rem 0', display: 'flex', alignItems: 'center', gap: '0.4rem'}}
              >
                ← Alle Dateien
              </button>
              <span style={{color: 'var(--muted)', fontSize: '0.85rem'}}>/</span>
              <span style={{color: 'var(--white)', fontSize: '0.85rem', fontWeight: 600}}>📁 {currentFolderName}</span>
            </div>
          ) : (
            /* Folder tiles */
            <div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center', paddingBottom: folders.length > 0 ? '0.75rem' : '0.25rem'}}>
                {folders.map(folder => (
                  <div
                    key={folder.id}
                    className="avs-folder-tile"
                    style={{
                      display: 'flex', flexDirection: 'column', gap: '0.35rem',
                      padding: '0.6rem 0.75rem',
                      background: 'rgba(18,112,196,0.08)',
                      border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
                      fontFamily: 'var(--font-head)', fontSize: '0.82rem', color: 'var(--white)',
                      minWidth: '130px', maxWidth: '200px',
                      cursor: 'pointer', transition: 'border-color 0.15s',
                      userSelect: 'none',
                    }}
                    onClick={() => navigateToFolder(folder.id, folder.name)}
                  >
                    {/* Row 1: icon + name */}
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                      <span style={{fontSize: '1rem', flexShrink: 0}}>📁</span>
                      <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, flex: 1}}>{folder.name}</span>
                    </div>
                    {/* Row 2: count + action buttons */}
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.4rem'}}>
                      <span style={{color: 'var(--muted)', fontSize: '0.68rem'}}>
                        {folder.file_count} {Number(folder.file_count) === 1 ? 'Datei' : 'Dateien'}
                      </span>
                      <div
                        className="avs-folder-actions"
                        style={{display: 'none', gap: '3px', flexShrink: 0}}
                        onClick={e => e.stopPropagation()}
                      >
                        <button
                          title="Umbenennen"
                          onClick={e => { e.stopPropagation(); setRenameFolderModal(folder); setRenameFolderName(folder.name); }}
                          style={{padding: '2px 7px', background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: '4px', color: '#fbbf24', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, lineHeight: 1}}
                        >✎</button>
                        <button
                          title="Löschen"
                          onClick={e => { e.stopPropagation(); setDeleteFolderDeleteFiles(false); setDeleteFolderConfirm(folder); }}
                          style={{padding: '2px 7px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '4px', color: '#f87171', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, lineHeight: 1}}
                        >✕</button>
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => { setNewFolderName(''); setNewFolderModal(true); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                    padding: '0.45rem 0.75rem', background: 'transparent',
                    border: '1px dashed rgba(18,112,196,0.4)', borderRadius: 'var(--r-md)',
                    color: 'var(--muted)', cursor: 'pointer', fontSize: '0.82rem',
                    fontFamily: 'var(--font-head)', fontWeight: 600, transition: 'border-color 0.15s, color 0.15s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--cyan)'; e.currentTarget.style.color = 'var(--cyan)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(18,112,196,0.4)'; e.currentTarget.style.color = 'var(--muted)'; }}
                >
                  <span style={{fontSize: '0.9rem'}}>+</span> Neuer Ordner
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Files Display */}
      <div style={{maxWidth: '1280px', margin: '0 auto', padding: '0.5rem 1.5rem 2rem'}}>
        {loadingFiles ? (
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5rem 0'}}>
            <div style={{width: '48px', height: '48px', border: '3px solid rgba(26,180,240,0.15)', borderTop: '3px solid var(--cyan)', borderRadius: '50%', animation: 'spin 1s linear infinite'}}></div>
          </div>
        ) : displayedFiles.length === 0 ? (
          <div style={{textAlign: 'center', padding: '5rem 0'}}>
            <div style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '80px', height: '80px', background: 'rgba(18,112,196,0.08)', border: '1px solid var(--border)', borderRadius: '50%', marginBottom: '1.5rem'}}>
              <svg style={{width: '36px', height: '36px', color: 'var(--muted)'}} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 style={{fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1.5rem', letterSpacing: '-0.02em', color: 'var(--white)', marginBottom: '0.5rem'}}>
              {searchQuery ? 'Keine Dateien gefunden' : 'Noch keine Dateien'}
            </h3>
            <p style={{color: 'var(--muted)', fontSize: '0.9rem', marginBottom: '1.5rem'}}>
              {searchQuery ? 'Versuche einen anderen Suchbegriff' : 'Lade deine erste Datei hoch'}
            </p>
            {!searchQuery && (
              <button
                onClick={onBackToUpload}
                className="avs-btn"
              >
                ↑ Jetzt hochladen
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View */
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem'}}>
            {displayedFiles.map((file) => (
              <div
                key={file.id}
                className="avs-card"
                style={{
                  padding: '1rem',
                  border: selectedFileIds.includes(file.id) ? '1px solid var(--border-hov)' : '1px solid var(--border)',
                  background: selectedFileIds.includes(file.id) ? 'rgba(18,112,196,0.08)' : 'var(--bg-card)',
                  boxShadow: selectedFileIds.includes(file.id) ? '0 0 20px rgba(26,180,240,0.1)' : undefined,
                  position: 'relative'
                }}
              >
                {/* Checkbox */}
                <button
                  onClick={(e) => { e.stopPropagation(); toggleFileSelection(file.id); }}
                  style={{
                    position: 'absolute', top: '10px', left: '10px', zIndex: 10,
                    width: '26px', height: '26px', borderRadius: '6px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: selectedFileIds.includes(file.id) ? 'linear-gradient(135deg, var(--cyan), var(--blue-mid))' : 'rgba(6,12,24,0.7)',
                    border: `1px solid ${selectedFileIds.includes(file.id) ? 'var(--cyan)' : 'rgba(18,112,196,0.3)'}`,
                    transition: 'all 0.2s'
                  }}
                  aria-label="Auswählen"
                >
                  {selectedFileIds.includes(file.id) && (
                    <svg style={{width: '14px', height: '14px', color: '#fff'}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>

                {/* File Icon / Thumbnail */}
                <div
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '100%', height: '120px', marginBottom: '0.875rem',
                    background: 'rgba(10,31,61,0.5)', borderRadius: 'var(--r-md)',
                    overflow: 'hidden', cursor: isImage(file.filename) || isVideo(file.filename) || isAudio(file.filename) ? 'pointer' : 'default',
                    transition: 'transform 0.2s'
                  }}
                  onClick={() => {
                    if (isImage(file.filename)) {
                      if (window.innerWidth < 768) { handleDownload(file.id, file.filename); }
                      else { setImagePreviewModal(file); loadImage(file.id); }
                    }
                    else if (isVideo(file.filename)) { loadVideo(file); }
                    else if (isAudio(file.filename)) { loadAudio(file); }
                  }}
                >
                  {isImage(file.filename) ? (
                    imageCache[file.id] ? (
                      <img src={imageCache[file.id]} alt={file.filename} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                    ) : (
                      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px'}}>
                        <div style={{width: '24px', height: '24px', border: '2px solid rgba(26,180,240,0.2)', borderTop: '2px solid var(--cyan)', borderRadius: '50%', animation: 'spin 1s linear infinite'}}></div>
                        <span style={{fontSize: '0.65rem', color: 'var(--muted)'}}>Lade...</span>
                      </div>
                    )
                  ) : (
                    <span style={{fontSize: '3rem'}}>{getFileIcon(file.filename)}</span>
                  )}
                </div>

                {/* File Info */}
                <div style={{marginBottom: '0.875rem'}}>
                  <h3 style={{fontFamily: 'var(--font-head)', fontWeight: 600, fontSize: '0.875rem', color: 'var(--white)', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={file.filename}>
                    {file.filename}
                  </h3>
                  {file.processing_status && file.processing_status !== 'ready' ? (
                    <div style={{fontSize: '0.7rem', color: file.processing_status === 'failed' ? '#f87171' : '#fbbf24', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px'}}>
                      {file.processing_status === 'failed' ? '✕ Verarbeitung fehlgeschlagen' : file.processing_status === 'scanning' ? '🔍 Virenscan...' : file.processing_status === 'encrypting' ? '🔒 Verschlüsselung...' : '⏳ Wird verarbeitet...'}
                    </div>
                  ) : (
                    <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: 'var(--muted)', fontFamily: 'var(--font-mono)'}}>
                      <span>{formatBytes(file.file_size)}</span>
                      <span>{formatDate(file.uploaded_at).split(',')[0]}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{display: 'flex', gap: '0.4rem'}}>
                  {[
                    {icon: 'ℹ', title: 'Info', color: 'rgba(139,92,246,0.15)', hover: 'rgba(139,92,246,0.25)', text: 'rgba(167,139,250,1)', action: () => handleInfo(file)},
                    {icon: '↓', title: 'Download', color: 'rgba(18,112,196,0.12)', hover: 'rgba(26,180,240,0.2)', text: 'var(--cyan)', action: () => handleDownload(file.id, file.filename)},
                    {icon: '✎', title: 'Umbenennen', color: 'rgba(251,191,36,0.08)', hover: 'rgba(251,191,36,0.18)', text: '#fbbf24', action: () => { setRenameModal(file); setRenameValue(file.filename); }},
                    ...(!teamContext ? [{icon: '⇲', title: 'Verschieben', color: 'rgba(16,185,129,0.08)', hover: 'rgba(16,185,129,0.18)', text: '#34d399', action: () => setMoveFileModal(file)}] : []),
                    {icon: '⇗', title: 'Teilen', color: 'rgba(18,112,196,0.08)', hover: 'rgba(18,112,196,0.18)', text: 'var(--white)', action: () => setShareModalFile(file)},
                    {icon: '✕', title: 'Löschen', color: 'rgba(239,68,68,0.08)', hover: 'rgba(239,68,68,0.18)', text: '#f87171', action: () => setDeleteConfirmFile(file)}
                  ].map(({icon, title, color, text, action}, i) => (
                    <button
                      key={i}
                      onClick={action}
                      title={title}
                      style={{
                        flex: i === 1 || i === 2 ? 1 : undefined,
                        padding: '0.45rem 0.6rem',
                        background: color,
                        border: '1px solid transparent',
                        borderRadius: 'var(--r-sm)',
                        color: text,
                        cursor: 'pointer',
                        fontFamily: 'var(--font-head)',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        transition: 'background 0.2s'
                      }}
                    >{icon}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="avs-card" style={{padding: 0, overflow: 'hidden'}}>
            <div style={{overflowX: 'auto', WebkitOverflowScrolling: 'touch'}}>
            <table style={{width: '100%', minWidth: '560px', borderCollapse: 'collapse'}}>
              <thead>
                <tr style={{borderBottom: '1px solid var(--border)'}}>
                  {['Name', 'Größe', 'Hochgeladen', 'Aktionen'].map((h, i) => (
                    <th key={h} style={{padding: '0.875rem 1.25rem', textAlign: i === 3 ? 'right' : 'left', fontSize: '0.65rem', fontFamily: 'var(--font-mono)', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedFiles.map((file, idx) => (
                  <tr key={file.id} style={{borderBottom: idx < displayedFiles.length - 1 ? '1px solid var(--border)' : 'none', transition: 'background 0.2s'}}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(18,112,196,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{padding: '0.875rem 1.25rem'}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                        <span style={{fontSize: '1.5rem'}}>{getFileIcon(file.filename)}</span>
                        <span style={{fontWeight: 500, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '300px'}} title={file.filename}>{file.filename}</span>
                      </div>
                    </td>
                    <td style={{padding: '0.875rem 1.25rem', fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: 'var(--muted)'}}>{formatBytes(file.file_size)}</td>
                    <td style={{padding: '0.875rem 1.25rem', fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: 'var(--muted)'}}>{formatDate(file.uploaded_at)}</td>
                    <td style={{padding: '0.875rem 1.25rem'}}>
                      <div style={{display: 'flex', justifyContent: 'flex-end', gap: '0.4rem'}}>
                        {[
                          {icon: '↓', title: 'Download', action: () => handleDownload(file.id, file.filename)},
                          {icon: '✎', title: 'Umbenennen', action: () => { setRenameModal(file); setRenameValue(file.filename); }},
                          ...(!teamContext ? [{icon: '⇲', title: 'Verschieben', action: () => setMoveFileModal(file), green: true}] : []),
                          {icon: '⇗', title: 'Teilen', action: () => setShareModalFile(file)},
                          {icon: '✕', title: 'Löschen', action: () => setDeleteConfirmFile(file), red: true}
                        ].map(({icon, title, action, red, green}, i) => (
                          <button key={i} onClick={action} title={title} style={{padding: '0.4rem 0.65rem', background: red ? 'rgba(239,68,68,0.08)' : green ? 'rgba(16,185,129,0.08)' : 'rgba(18,112,196,0.08)', border: '1px solid transparent', borderRadius: 'var(--r-sm)', color: red ? '#f87171' : green ? '#34d399' : 'var(--cyan)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, transition: 'background 0.2s'}}>{icon}</button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* Load More — nur im eigenen Speicher */}
        {!teamContext && currentPage < totalPages && !loadingFiles && (
          <div style={{marginTop: '1.5rem', textAlign: 'center'}}>
            <button onClick={loadMore} disabled={loadingMore} className="avs-btn-ghost" style={{padding: '0.75rem 2rem', opacity: loadingMore ? 0.5 : 1}}>
              {loadingMore ? 'Lädt...' : `Mehr laden (${files.length} / ${totalFiles})`}
            </button>
          </div>
        )}
      </div>

      {/* Share Modal */}
      {shareModalFile && (
        <ShareLinkModal file={shareModalFile} token={token} onClose={() => setShareModalFile(null)} onCreateLink={(linkData) => { console.log('Share link created:', linkData); }} />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmFile && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem'}}>
          <BodyScrollLock />
          <div className="avs-card" style={{maxWidth: '440px', width: '100%', padding: '2rem', maxHeight: '85vh', overflowY: 'auto', margin: 'auto 0', maxHeight: '90vh', overflowY: 'auto'}}>
            <h3 style={{fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.02em', color: 'var(--white)', marginBottom: '0.75rem'}}>Datei löschen?</h3>
            <p style={{color: 'var(--muted)', fontSize: '0.875rem', lineHeight: 1.7, marginBottom: '1.5rem'}}>
              Möchtest du <span style={{color: 'var(--white)', fontWeight: 600}}>{deleteConfirmFile.filename}</span> wirklich löschen? Diese Aktion ist unwiderruflich.
            </p>
            <div style={{display: 'flex', gap: '0.75rem'}}>
              <button onClick={() => handleDelete(deleteConfirmFile.id)} style={{flex: 1, padding: '0.75rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--r-md)', color: '#f87171', cursor: 'pointer', fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '0.875rem'}}>Löschen</button>
              <button onClick={() => setDeleteConfirmFile(null)} className="avs-btn-ghost" style={{padding: '0.75rem 1.5rem', fontSize: '0.875rem'}}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* Share Links Manager */}
      {showShareLinksManager && (<ShareLinksManager token={token} onClose={() => setShowShareLinksManager(false)} />)}

      {/* Admin Panel */}
      {showAdminPanel && (<AdminPanel token={token} onClose={() => setShowAdminPanel(false)} />)}

      {/* Abuse Report Modal — erscheint wenn Nutzer Melde-Button aus ShareLinksManager nutzt */}
      {abuseReportModal && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem'}}>
          <BodyScrollLock />
          <div className="avs-card" style={{maxWidth: '480px', width: '100%', padding: '2rem', maxHeight: '85vh', overflowY: 'auto', margin: 'auto 0', maxHeight: '90vh', overflowY: 'auto'}}>
            {abuseSuccess ? (
              <div style={{textAlign: 'center', padding: '1rem 0'}}>
                <div style={{fontSize: '2.5rem', marginBottom: '0.75rem'}}>✅</div>
                <h3 style={{fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1.1rem', color: 'var(--white)', marginBottom: '0.5rem'}}>Meldung erhalten</h3>
                <p style={{color: 'var(--muted)', fontSize: '0.875rem', lineHeight: 1.7, marginBottom: '1.5rem'}}>
                  Danke für deine Meldung. Sie wird geprüft und bei Bedarf werden wir tätig.
                </p>
                <button onClick={closeAbuseModal} className="avs-btn-ghost" style={{padding: '0.75rem 2rem', fontSize: '0.875rem'}}>Schließen</button>
              </div>
            ) : (
              <>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                    <div style={{width: '38px', height: '38px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--r-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem'}}>🚨</div>
                    <div>
                      <h3 style={{fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '1rem', color: 'var(--white)'}}>Inhalt melden</h3>
                      <p style={{fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--muted)', letterSpacing: '0.08em', textTransform: 'uppercase'}}>Missbrauchsmeldung</p>
                    </div>
                  </div>
                  <button onClick={closeAbuseModal} style={{background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: '1.1rem'}}>✕</button>
                </div>

                <div style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.5rem'}}>Art der Meldung *</label>
                  <select
                    value={abuseType}
                    onChange={e => setAbuseType(e.target.value)}
                    style={{width: '100%', padding: '0.7rem 1rem', background: 'rgba(10,31,61,0.6)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', color: abuseType ? 'var(--white)' : 'var(--muted)', fontSize: '0.875rem', outline: 'none', cursor: 'pointer'}}
                  >
                    <option value="" disabled>Bitte wählen...</option>
                    <option value="illegal_content">Illegaler Inhalt</option>
                    <option value="copyright">Urheberrechtsverletzung</option>
                    <option value="privacy">Datenschutzverletzung</option>
                    <option value="spam">Spam</option>
                    <option value="other">Sonstiges</option>
                  </select>
                </div>

                <div style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.5rem'}}>Beschreibung <span style={{fontWeight: 400, textTransform: 'none', letterSpacing: 0}}>(optional)</span></label>
                  <textarea
                    value={abuseDescription}
                    onChange={e => { if (e.target.value.length <= 500) setAbuseDescription(e.target.value); }}
                    placeholder="Kurze Beschreibung des Problems..."
                    rows={3}
                    style={{width: '100%', padding: '0.7rem 1rem', background: 'rgba(10,31,61,0.6)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', color: 'var(--white)', fontSize: '0.875rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box'}}
                  />
                  <p style={{fontSize: '0.72rem', color: 'var(--muted)', textAlign: 'right', marginTop: '0.25rem'}}>{abuseDescription.length}/500</p>
                </div>

                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.5rem'}}>E-Mail für Rückmeldung <span style={{fontWeight: 400, textTransform: 'none', letterSpacing: 0}}>(optional)</span></label>
                  <input
                    type="email"
                    value={abuseEmail}
                    onChange={e => setAbuseEmail(e.target.value)}
                    placeholder="deine@email.de"
                    style={{width: '100%', padding: '0.7rem 1rem', background: 'rgba(10,31,61,0.6)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', color: 'var(--white)', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box'}}
                  />
                </div>

                <div style={{display: 'flex', gap: '0.75rem'}}>
                  <button
                    onClick={handleAbuseSubmit}
                    disabled={!abuseType || abuseSubmitting}
                    style={{flex: 1, padding: '0.75rem', background: abuseType ? 'rgba(239,68,68,0.15)' : 'rgba(100,100,100,0.1)', border: `1px solid ${abuseType ? 'rgba(239,68,68,0.4)' : 'var(--border)'}`, borderRadius: 'var(--r-md)', color: abuseType ? '#f87171' : 'var(--muted)', cursor: abuseType ? 'pointer' : 'not-allowed', fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '0.875rem', transition: 'all 0.2s'}}
                  >
                    {abuseSubmitting ? 'Sende...' : 'Meldung absenden'}
                  </button>
                  <button onClick={closeAbuseModal} className="avs-btn-ghost" style={{padding: '0.75rem 1.25rem', fontSize: '0.875rem'}}>Abbrechen</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* spin + storage animations */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes avs-pulse-red {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
        .avs-shimmer { animation: avs-shimmer-move 2s linear infinite; }
        @keyframes avs-shimmer-move { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
      `}</style>

      {/* Audio Player Modal */}
      {audioPlayerModal && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(16px)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', overflow: 'hidden'}} onClick={closeAudioPlayer}>
          <BodyScrollLock />
          <div style={{position: 'relative', width: '100%', maxWidth: '520px'}} onClick={e => e.stopPropagation()}>
            <div className="avs-card" style={{padding: '2rem'}}>
              <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem'}}>
                <button onClick={closeAudioPlayer} style={{padding: '0.4rem 0.6rem', background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', color: 'var(--white)', cursor: 'pointer', lineHeight: 1, fontSize: '1.1rem'}}>✕</button>
              </div>
              <div style={{textAlign: 'center', marginBottom: '1.5rem'}}>
                <div style={{width: '72px', height: '72px', margin: '0 auto 1rem', background: 'rgba(18,112,196,0.1)', border: '1px solid var(--border)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem'}}>🎵</div>
                <h3 style={{fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '1rem', color: 'var(--white)', marginBottom: '0.25rem'}}>{audioPlayerModal.filename}</h3>
                <span style={{fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--muted)'}}>{formatBytes(audioPlayerModal.file_size)}</span>
              </div>
              {loadingAudio ? (
                <div style={{textAlign: 'center', padding: '2rem 0'}}>
                  <div style={{width: '32px', height: '32px', border: '2px solid rgba(26,180,240,0.2)', borderTop: '2px solid var(--cyan)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 0.75rem'}}></div>
                  <p style={{color: 'var(--muted)', fontSize: '0.85rem'}}>Audio wird geladen...</p>
                </div>
              ) : audioUrl ? (
                <audio src={audioUrl} controls autoPlay className="w-full" style={{width: '100%'}} />
              ) : (
                <p style={{color: '#f87171', textAlign: 'center', fontSize: '0.875rem'}}>Audio konnte nicht geladen werden</p>
              )}
              <div style={{marginTop: '1.25rem', display: 'flex', gap: '0.75rem'}}>
                <button onClick={() => handleDownload(audioPlayerModal.id, audioPlayerModal.filename)} className="avs-btn" style={{flex: 1, fontSize: '0.85rem', padding: '0.65rem'}}>↓ Download</button>
                <button onClick={closeAudioPlayer} className="avs-btn-ghost" style={{padding: '0.65rem 1.25rem', fontSize: '0.85rem'}}>Schließen</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {videoPlayerModal && (
        <div style={{position: 'fixed', inset: 0, background: '#000', zIndex: 50, display: 'flex', flexDirection: 'column', overflow: 'hidden'}} onClick={closeVideoPlayer}>
          <BodyScrollLock />
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem', background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(10px)'}} onClick={e => e.stopPropagation()}>
            <div>
              <p style={{fontFamily: 'var(--font-head)', fontWeight: 600, fontSize: '0.9rem', color: 'var(--white)'}}>{videoPlayerModal.filename}</p>
              <p style={{fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--muted)'}}>{formatBytes(videoPlayerModal.file_size)}</p>
            </div>
            <button onClick={closeVideoPlayer} style={{padding: '0.5rem', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', color: 'var(--white)', cursor: 'pointer', fontSize: '1rem'}}>✕</button>
          </div>
          <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'}} onClick={e => e.stopPropagation()}>
            {loadingVideo ? (
              <div style={{textAlign: 'center'}}>
                <div style={{width: '48px', height: '48px', border: '3px solid rgba(26,180,240,0.2)', borderTop: '3px solid var(--cyan)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem'}}></div>
                <p style={{color: 'var(--muted)', fontSize: '0.875rem'}}>Video wird geladen...</p>
              </div>
            ) : videoUrl ? (
              <video src={videoUrl} controls autoPlay playsInline style={{maxWidth: '100%', maxHeight: '80vh', borderRadius: 'var(--r-lg)'}} />
            ) : (
              <p style={{color: '#f87171'}}>Video konnte nicht geladen werden</p>
            )}
          </div>
          <div style={{padding: '1rem 1.5rem', background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(10px)', display: 'flex', gap: '0.75rem'}} onClick={e => e.stopPropagation()}>
            <button onClick={() => handleDownload(videoPlayerModal.id, videoPlayerModal.filename)} className="avs-btn" style={{flex: 1, fontSize: '0.85rem', padding: '0.65rem'}}>↓ Download</button>
            <button onClick={closeVideoPlayer} className="avs-btn-ghost" style={{padding: '0.65rem 1.5rem', fontSize: '0.85rem'}}>Schließen</button>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {imagePreviewModal && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(16px)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', overflow: 'hidden'}} onClick={() => setImagePreviewModal(null)}>
          <BodyScrollLock />
          <button onClick={() => setImagePreviewModal(null)} style={{position: 'fixed', top: '16px', right: '16px', padding: '0.5rem', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', color: 'var(--white)', cursor: 'pointer', fontSize: '1rem', zIndex: 10000}}>✕</button>
          <div style={{position: 'absolute', top: '1rem', left: '1rem', maxWidth: 'calc(100vw - 6rem)', background: 'rgba(6,12,24,0.8)', backdropFilter: 'blur(8px)', padding: '0.5rem 1rem', borderRadius: 'var(--r-md)', border: '1px solid var(--border)', overflow: 'hidden'}}>
            <p style={{fontFamily: 'var(--font-head)', fontWeight: 600, fontSize: '0.875rem', color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{imagePreviewModal.filename}</p>
            <p style={{fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--muted)'}}>{formatBytes(imagePreviewModal.file_size)}</p>
          </div>
          <div onClick={e => e.stopPropagation()}>
            {imageCache[imagePreviewModal.id] ? (
              <img src={imageCache[imagePreviewModal.id]} alt={imagePreviewModal.filename} style={{maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 'var(--r-lg)', boxShadow: 'var(--shadow-card)'}} />
            ) : (
              <div style={{textAlign: 'center'}}>
                <div style={{width: '48px', height: '48px', border: '3px solid rgba(26,180,240,0.2)', borderTop: '3px solid var(--cyan)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem'}}></div>
                <p style={{color: 'var(--white)'}}>Lade Bild...</p>
              </div>
            )}
          </div>
          <div style={{position: 'absolute', bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))', left: '50%', transform: 'translateX(-50%)'}}>
            <button onClick={e => { e.stopPropagation(); handleDownload(imagePreviewModal.id, imagePreviewModal.filename); }} className="avs-btn" style={{fontSize: '0.85rem', padding: '0.65rem 1.5rem', whiteSpace: 'nowrap'}}>↓ Download</button>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem'}}>
          <BodyScrollLock />
          <div className="avs-card" style={{maxWidth: '520px', width: '100%', padding: '2rem', maxHeight: '80vh', overflowY: 'auto'}}>
            <h3 style={{fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1.2rem', letterSpacing: '-0.02em', color: 'var(--white)', marginBottom: '0.75rem'}}>
              {selectedFileIds.length} {selectedFileIds.length === 1 ? 'Datei' : 'Dateien'} löschen?
            </h3>
            <p style={{color: 'var(--muted)', fontSize: '0.875rem', lineHeight: 1.7, marginBottom: '1rem'}}>Diese Aktion ist unwiderruflich.</p>
            <div style={{background: 'rgba(6,12,24,0.6)', borderRadius: 'var(--r-md)', padding: '0.875rem', marginBottom: '1.25rem', maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border)'}}>
              {displayedFiles.filter(f => selectedFileIds.includes(f.id)).map(file => (
                <div key={file.id} style={{display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.4rem 0', fontSize: '0.82rem', borderBottom: '1px solid rgba(18,112,196,0.06)'}}>
                  <span>{getFileIcon(file.filename)}</span>
                  <span style={{flex: 1, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{file.filename}</span>
                  <span style={{fontFamily: 'var(--font-mono)', color: 'var(--muted)', fontSize: '0.7rem'}}>{formatBytes(file.file_size)}</span>
                </div>
              ))}
            </div>
            <div style={{display: 'flex', gap: '0.75rem'}}>
              <button onClick={handleBulkDelete} style={{flex: 1, padding: '0.75rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--r-md)', color: '#f87171', cursor: 'pointer', fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '0.875rem'}}>Alle löschen</button>
              <button onClick={() => setShowBulkDeleteConfirm(false)} className="avs-btn-ghost" style={{padding: '0.75rem 1.5rem', fontSize: '0.875rem'}}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* File Info Modal */}
      {infoModalFile && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem'}}>
          <BodyScrollLock />
          <div className="avs-card" style={{maxWidth: '520px', width: '100%', padding: '1.75rem', maxHeight: '85vh', overflowY: 'auto'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                <div style={{width: '38px', height: '38px', background: 'rgba(18,112,196,0.1)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem'}}>ℹ</div>
                <div>
                  <h3 style={{fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '1rem', color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 'min(300px, calc(100vw - 140px))'}}>{infoModalFile.filename}</h3>
                  <p style={{fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--muted)', letterSpacing: '0.08em', textTransform: 'uppercase'}}>Datei-Info</p>
                </div>
              </div>
              <button onClick={() => setInfoModalFile(null)} style={{background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: '1.1rem'}}>✕</button>
            </div>

            {/* Basic info */}
            <div style={{background: 'rgba(10,31,61,0.4)', borderRadius: 'var(--r-md)', padding: '1rem', marginBottom: '1rem', border: '1px solid var(--border)'}}>
              <p className="avs-label" style={{marginBottom: '0.75rem'}}>Datei</p>
              {[
                {k: 'Größe', v: formatBytes(infoModalFile.file_size)},
                {k: 'Typ', v: infoModalFile.mime_type || 'Unbekannt'},
                {k: 'Hochgeladen', v: formatDate(infoModalFile.uploaded_at)}
              ].map(({k, v}) => (
                <div key={k} style={{display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px solid rgba(18,112,196,0.06)', fontSize: '0.82rem'}}>
                  <span style={{color: 'var(--muted)'}}>{k}</span>
                  <span style={{color: 'var(--white)', fontWeight: 500, fontFamily: 'var(--font-mono)', fontSize: '0.78rem'}}>{v}</span>
                </div>
              ))}
            </div>

            {loadingMetadata && (
              <div style={{textAlign: 'center', padding: '1.5rem 0'}}>
                <div style={{width: '28px', height: '28px', border: '2px solid rgba(26,180,240,0.2)', borderTop: '2px solid var(--cyan)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto'}}></div>
              </div>
            )}

            {!loadingMetadata && fileMetadata && (
              <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                {fileMetadata.camera && (
                  <div style={{background: 'rgba(10,31,61,0.4)', borderRadius: 'var(--r-md)', padding: '1rem', border: '1px solid var(--border)'}}>
                    <p className="avs-label" style={{marginBottom: '0.75rem'}}>Kamera</p>
                    {fileMetadata.camera.make && <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '0.25rem 0'}}><span style={{color: 'var(--muted)'}}>Modell</span><span style={{color: 'var(--white)', fontFamily: 'var(--font-mono)', fontSize: '0.78rem'}}>{fileMetadata.camera.make} {fileMetadata.camera.model}</span></div>}
                    {fileMetadata.camera.software && <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '0.25rem 0'}}><span style={{color: 'var(--muted)'}}>Software</span><span style={{color: 'var(--white)', fontFamily: 'var(--font-mono)', fontSize: '0.78rem'}}>{fileMetadata.camera.software}</span></div>}
                  </div>
                )}
                {fileMetadata.technical && (
                  <div style={{background: 'rgba(10,31,61,0.4)', borderRadius: 'var(--r-md)', padding: '1rem', border: '1px solid var(--border)'}}>
                    <p className="avs-label" style={{marginBottom: '0.75rem'}}>Technisch</p>
                    {fileMetadata.technical.width && <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '0.25rem 0'}}><span style={{color: 'var(--muted)'}}>Auflösung</span><span style={{color: 'var(--white)', fontFamily: 'var(--font-mono)', fontSize: '0.78rem'}}>{fileMetadata.technical.width}×{fileMetadata.technical.height}</span></div>}
                    {fileMetadata.technical.iso && <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '0.25rem 0'}}><span style={{color: 'var(--muted)'}}>ISO</span><span style={{color: 'var(--white)', fontFamily: 'var(--font-mono)', fontSize: '0.78rem'}}>{fileMetadata.technical.iso}</span></div>}
                    {fileMetadata.technical.aperture && <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '0.25rem 0'}}><span style={{color: 'var(--muted)'}}>Blende</span><span style={{color: 'var(--white)', fontFamily: 'var(--font-mono)', fontSize: '0.78rem'}}>f/{fileMetadata.technical.aperture}</span></div>}
                    {fileMetadata.technical.focal_length && <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '0.25rem 0'}}><span style={{color: 'var(--muted)'}}>Brennweite</span><span style={{color: 'var(--white)', fontFamily: 'var(--font-mono)', fontSize: '0.78rem'}}>{fileMetadata.technical.focal_length}mm</span></div>}
                  </div>
                )}
                {fileMetadata.gps && (
                  <div style={{background: 'rgba(10,31,61,0.4)', borderRadius: 'var(--r-md)', padding: '1rem', border: '1px solid var(--border)'}}>
                    <p className="avs-label" style={{marginBottom: '0.75rem'}}>GPS</p>
                    <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '0.25rem 0'}}><span style={{color: 'var(--muted)'}}>Koordinaten</span><span style={{color: 'var(--white)', fontFamily: 'var(--font-mono)', fontSize: '0.78rem'}}>{fileMetadata.gps.latitude.toFixed(6)}, {fileMetadata.gps.longitude.toFixed(6)}</span></div>
                    <a href={`https://www.google.com/maps?q=${fileMetadata.gps.latitude},${fileMetadata.gps.longitude}`} target="_blank" rel="noopener noreferrer" style={{color: 'var(--cyan)', fontSize: '0.8rem', fontWeight: 600}}>↗ Google Maps öffnen</a>
                  </div>
                )}
              </div>
            )}

            {!loadingMetadata && !fileMetadata && (
              <div style={{textAlign: 'center', padding: '1.5rem 0'}}>
                <p style={{color: 'var(--muted)', fontSize: '0.875rem'}}>Keine erweiterten Metadaten verfügbar</p>
              </div>
            )}

            <button onClick={() => setInfoModalFile(null)} className="avs-btn-ghost" style={{width: '100%', padding: '0.75rem', fontSize: '0.875rem', marginTop: '1.25rem', justifyContent: 'center', borderRadius: 'var(--r-md)', border: '1px solid var(--border)'}}>Schließen</button>
          </div>
        </div>
      )}

      {/* New Folder Modal */}
      {newFolderModal && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem'}} onClick={() => setNewFolderModal(false)}>
          <BodyScrollLock />
          <div className="avs-card" style={{maxWidth: '400px', width: '100%', padding: '1.75rem'}} onClick={e => e.stopPropagation()}>
            <h3 style={{fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1.1rem', color: 'var(--white)', marginBottom: '1.25rem'}}>📁 Neuer Ordner</h3>
            <input
              autoFocus
              type="text"
              placeholder="Ordnername..."
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') setNewFolderModal(false); }}
              className="avs-input"
              style={{marginBottom: '1rem'}}
              maxLength={100}
            />
            <div style={{display: 'flex', gap: '0.75rem'}}>
              <button onClick={handleCreateFolder} disabled={newFolderSaving || !newFolderName.trim()} className="avs-btn" style={{flex: 1, opacity: newFolderSaving || !newFolderName.trim() ? 0.5 : 1}}>
                {newFolderSaving ? 'Erstelle...' : 'Erstellen'}
              </button>
              <button onClick={() => setNewFolderModal(false)} className="avs-btn-ghost" style={{padding: '0.75rem 1.25rem'}}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {renameFolderModal && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem'}} onClick={() => setRenameFolderModal(null)}>
          <BodyScrollLock />
          <div className="avs-card" style={{maxWidth: '400px', width: '100%', padding: '1.75rem'}} onClick={e => e.stopPropagation()}>
            <h3 style={{fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1.1rem', color: 'var(--white)', marginBottom: '1.25rem'}}>✎ Ordner umbenennen</h3>
            <input
              autoFocus
              type="text"
              value={renameFolderName}
              onChange={e => setRenameFolderName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleRenameFolder(); if (e.key === 'Escape') setRenameFolderModal(null); }}
              className="avs-input"
              style={{marginBottom: '1rem'}}
              maxLength={100}
            />
            <div style={{display: 'flex', gap: '0.75rem'}}>
              <button onClick={handleRenameFolder} disabled={!renameFolderName.trim()} className="avs-btn" style={{flex: 1, opacity: !renameFolderName.trim() ? 0.5 : 1}}>Umbenennen</button>
              <button onClick={() => setRenameFolderModal(null)} className="avs-btn-ghost" style={{padding: '0.75rem 1.25rem'}}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Folder Confirm Modal */}
      {deleteFolderConfirm && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem'}} onClick={() => { setDeleteFolderConfirm(null); setDeleteFolderDeleteFiles(false); }}>
          <BodyScrollLock />
          <div className="avs-card" style={{maxWidth: '440px', width: '100%', padding: '1.75rem'}} onClick={e => e.stopPropagation()}>
            <h3 style={{fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1.1rem', color: 'var(--white)', marginBottom: '0.75rem'}}>Ordner löschen?</h3>
            <p style={{color: 'var(--muted)', fontSize: '0.875rem', lineHeight: 1.7, marginBottom: '1.25rem'}}>
              Ordner <span style={{color: 'var(--white)', fontWeight: 600}}>📁 {deleteFolderConfirm.name}</span> löschen?{' '}
              {Number(deleteFolderConfirm.file_count) > 0 && (
                <span>Er enthält <span style={{color: 'var(--white)', fontWeight: 600}}>{deleteFolderConfirm.file_count} {Number(deleteFolderConfirm.file_count) === 1 ? 'Datei' : 'Dateien'}</span>.</span>
              )}
            </p>

            {/* Checkbox: was passiert mit den enthaltenen Dateien */}
            {Number(deleteFolderConfirm.file_count) > 0 && (
              <label style={{display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.875rem', background: deleteFolderDeleteFiles ? 'rgba(239,68,68,0.08)' : 'rgba(18,112,196,0.06)', border: `1px solid ${deleteFolderDeleteFiles ? 'rgba(239,68,68,0.25)' : 'var(--border)'}`, borderRadius: 'var(--r-md)', marginBottom: '1.25rem', cursor: 'pointer', transition: 'all 0.15s'}}>
                <input
                  type="checkbox"
                  checked={deleteFolderDeleteFiles}
                  onChange={e => setDeleteFolderDeleteFiles(e.target.checked)}
                  style={{marginTop: '2px', width: '16px', height: '16px', accentColor: '#f87171', flexShrink: 0, cursor: 'pointer'}}
                />
                <div>
                  <div style={{fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '0.85rem', color: deleteFolderDeleteFiles ? '#f87171' : 'var(--white)', marginBottom: '0.2rem'}}>
                    Enthaltene Dateien ebenfalls löschen
                  </div>
                  <div style={{fontSize: '0.78rem', color: 'var(--muted)', lineHeight: 1.5}}>
                    {deleteFolderDeleteFiles
                      ? 'Alle Dateien im Ordner werden unwiderruflich gelöscht.'
                      : 'Dateien werden in den normalen Speicher verschoben und bleiben erhalten.'}
                  </div>
                </div>
              </label>
            )}

            <div style={{display: 'flex', gap: '0.75rem'}}>
              <button
                onClick={() => handleDeleteFolder(deleteFolderConfirm, deleteFolderDeleteFiles)}
                style={{flex: 1, padding: '0.75rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--r-md)', color: '#f87171', cursor: 'pointer', fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '0.875rem'}}
              >
                {deleteFolderDeleteFiles ? 'Ordner + Dateien löschen' : 'Ordner löschen'}
              </button>
              <button onClick={() => { setDeleteFolderConfirm(null); setDeleteFolderDeleteFiles(false); }} className="avs-btn-ghost" style={{padding: '0.75rem 1.5rem', fontSize: '0.875rem'}}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* Move Files Modal */}
      {moveFileModal && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem'}} onClick={() => setMoveFileModal(null)}>
          <BodyScrollLock />
          <div className="avs-card" style={{maxWidth: '400px', width: '100%', padding: '1.75rem', maxHeight: '80vh', overflowY: 'auto'}} onClick={e => e.stopPropagation()}>
            <h3 style={{fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: '1.1rem', color: 'var(--white)', marginBottom: '0.5rem'}}>⇲ Dateien verschieben</h3>
            <p style={{color: 'var(--muted)', fontSize: '0.8rem', marginBottom: '1.25rem'}}>
              {moveFileModal === 'bulk' ? `${selectedFileIds.length} Datei(en) verschieben nach:` : `"${moveFileModal.filename}" verschieben nach:`}
            </p>
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.25rem'}}>
              <button
                onClick={() => handleMoveFiles(moveFileModal === 'bulk' ? selectedFileIds : [moveFileModal.id], null)}
                style={{display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 1rem', background: currentFolderId === null ? 'rgba(18,112,196,0.12)' : 'transparent', border: `1px solid ${currentFolderId === null ? 'var(--border-hov)' : 'var(--border)'}`, borderRadius: 'var(--r-md)', color: 'var(--muted)', cursor: currentFolderId === null ? 'default' : 'pointer', fontFamily: 'var(--font-head)', fontSize: '0.85rem', textAlign: 'left', opacity: currentFolderId === null ? 0.5 : 1}}
                disabled={currentFolderId === null}
              >
                <span>🏠</span> Root (kein Ordner)
              </button>
              {folders.map(folder => (
                <button
                  key={folder.id}
                  onClick={() => handleMoveFiles(moveFileModal === 'bulk' ? selectedFileIds : [moveFileModal.id], folder.id)}
                  disabled={currentFolderId === folder.id}
                  style={{display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 1rem', background: currentFolderId === folder.id ? 'rgba(18,112,196,0.12)' : 'transparent', border: `1px solid ${currentFolderId === folder.id ? 'var(--border-hov)' : 'var(--border)'}`, borderRadius: 'var(--r-md)', color: currentFolderId === folder.id ? 'var(--muted)' : 'var(--white)', cursor: currentFolderId === folder.id ? 'default' : 'pointer', fontFamily: 'var(--font-head)', fontSize: '0.85rem', textAlign: 'left', opacity: currentFolderId === folder.id ? 0.5 : 1}}
                >
                  <span>📁</span>
                  <span style={{flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{folder.name}</span>
                  <span style={{color: 'var(--muted)', fontSize: '0.72rem'}}>({folder.file_count})</span>
                </button>
              ))}
              {folders.length === 0 && (
                <p style={{color: 'var(--muted)', fontSize: '0.82rem', textAlign: 'center', padding: '0.75rem'}}>Noch keine Ordner erstellt. Erstelle zuerst einen Ordner.</p>
              )}
            </div>
            <button onClick={() => setMoveFileModal(null)} className="avs-btn-ghost" style={{width: '100%', padding: '0.65rem', fontSize: '0.85rem'}}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* Team Manager Modal */}
      {showTeamManager && (
        <TeamManager
          token={token}
          onClose={() => { setShowTeamManager(false); loadPendingInvCount(); }}
          onSwitchStorage={(ctx) => { setShowTeamManager(false); switchToTeam(ctx); }}
        />
      )}

      {/* Team Loading Overlay */}
      {loadingTeamFiles && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem'}}>
          <div style={{width: '44px', height: '44px', border: '3px solid rgba(168,85,247,0.2)', borderTop: '3px solid #a855f7', borderRadius: '50%', animation: 'spin 1s linear infinite'}} />
          <p style={{color: '#dceeff', fontWeight: 600}}>Lade Team-Speicher...</p>
        </div>
      )}

      {/* Download Progress Bar */}
      {Object.keys(activeDownloads).length > 0 && (
        <div style={{
          position: 'fixed', bottom: '1.5rem', left: '1rem', zIndex: 9998,
          maxWidth: '320px', width: '90%',
        }}>
          {Object.entries(activeDownloads).map(([id, dl]) => (
            <div key={id} style={{
              background: 'rgba(17,17,22,0.95)', border: '1px solid rgba(0,212,255,0.3)',
              borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '0.5rem',
              backdropFilter: 'blur(12px)',
            }}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px'}}>
                <span style={{fontSize: '0.78rem', color: 'var(--white)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px'}}>{dl.filename}</span>
                <span style={{fontSize: '0.72rem', color: 'var(--cyan)', fontWeight: 600}}>{dl.progress < 0 ? 'Lädt...' : dl.progress + '%'}</span>
              </div>
              <div style={{height: '3px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden'}}>
                <div style={{height: '100%', width: dl.progress < 0 ? '100%' : dl.progress + '%', opacity: dl.progress < 0 ? 0.4 : 1, background: 'var(--cyan)', borderRadius: '2px', transition: 'width 0.3s ease'}} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Dashboard;
